// `index`/`total` describe a position within a gallery. Both are optional and
// callers routinely pass null for "no position", so the pair is only honoured
// when BOTH are real integers and the gallery actually has more than one photo.
export type ImageAltOptions = {
  fallback?: string;
  index?: number | null;
  total?: number | null;
};

// Number.isInteger is declared as (value: unknown) => boolean, so it narrows
// nothing on its own. This predicate wrapper makes the SAME runtime call while
// letting the compiler see that a passing value is a number — without which the
// `total > 1` comparison and the `index + 1` arithmetic below would each need a
// cast. Behaviour is identical; only the declared type differs.
const isInteger = (value: unknown): value is number => Number.isInteger(value);

// `name` is deliberately `unknown` rather than `string`: every call site guards
// on `typeof name === 'string'` inside, precisely because the value arrives from
// database rows and user input where it can be null, a number, or missing.
export function imageAltText(name: unknown, {fallback = 'Photo', index = null, total = null}: ImageAltOptions = {}) {
  const label = typeof name === 'string' && name.trim() ? name.trim() : fallback;
  const hasPosition = isInteger(index) && isInteger(total) && total > 1;
  return hasPosition ? `${label} ${index + 1} of ${total}` : label;
}
